import { signal, computed } from "@preact/signals";
import type { MenuSnapshot, MenuItem, MenuItemId } from "@orderman/shared";
import { menu, soldCounts, saveMenu, LOW_STOCK } from "../../state/store.js";

// Leichtgewichtiger Verfügbarkeits-/Mengen-Editor: kippt pro Artikel nur `isAvailable` und
// `stock` und speichert einen menu-config.snapshot. Der volle Speisekarten-Editor (Kategorien/
// Preise/Modifier) bleibt bewusst im Cloud-/Console-Admin.

/** Lokale Arbeitskopie der Änderungen (menuItemId → Patch), bis „Speichern". */
const patch = signal<Record<string, { isAvailable?: boolean; stock?: number | null }>>({});
const saving = signal(false);
const flash = signal<string | null>(null);

const dirty = computed(() => Object.keys(patch.value).length > 0);

function effItem(item: MenuItem): MenuItem {
  const p = patch.value[item.id as string];
  if (!p) return item;
  const next: MenuItem = { ...item };
  if (p.isAvailable !== undefined) next.isAvailable = p.isAvailable;
  if (p.stock !== undefined) {
    if (p.stock === null) delete next.stock; // ∞ = Property entfernen (unbegrenzt)
    else next.stock = p.stock;
  }
  return next;
}

function setPatch(id: string, change: { isAvailable?: boolean; stock?: number | null }): void {
  patch.value = { ...patch.value, [id]: { ...patch.value[id], ...change } };
}

function remainingLabel(item: MenuItem): string | null {
  if (item.stock == null) return null;
  return `${item.stock - (soldCounts.value[item.id as string] ?? 0)} übrig`;
}

function save(): void {
  const m = menu.value;
  if (!m || !dirty.value) return;
  saving.value = true;
  const next: MenuSnapshot = { ...m, items: m.items.map((it) => effItem(it)) };
  saveMenu(next);
  patch.value = {};
  saving.value = false;
  flash.value = "Gespeichert – wird an alle Geräte verteilt.";
  setTimeout(() => (flash.value = null), 2500);
}

function discard(): void {
  patch.value = {};
}

export function AvailabilityView() {
  const m = menu.value;
  if (!m) return <div class="admin-empty">Keine Speisekarte geladen.</div>;

  const byCat = m.categories
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((c) => ({
      cat: c,
      items: m.items
        .filter((i) => i.categoryId === c.id && !i.isInfo)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <div class="admin-scroll">
      {byCat.map(({ cat, items }) => (
        <section key={cat.id} class="av-cat">
          <h3 class="av-cat-title">{cat.name}</h3>
          {items.map((raw) => {
            const item = effItem(raw);
            const id = item.id as MenuItemId as string;
            const rem = remainingLabel(item);
            const remLow =
              item.stock != null && item.stock - (soldCounts.value[id] ?? 0) <= LOW_STOCK;
            return (
              <div key={id} class={`av-row ${item.isAvailable ? "" : "off"}`}>
                <div class="av-main">
                  <div class="av-name">{item.name}</div>
                  <div class="av-sub">
                    {item.stock == null ? (
                      <span class="av-unlimited">∞ unbegrenzt</span>
                    ) : (
                      <span class={remLow ? "av-rem low" : "av-rem"}>{rem}</span>
                    )}
                  </div>
                </div>

                <div class="av-stock">
                  {item.stock == null ? (
                    <button class="av-limit-set" onClick={() => setPatch(id, { stock: 50 })}>
                      Menge…
                    </button>
                  ) : (
                    <div class="av-stepper">
                      <button
                        aria-label="weniger"
                        onClick={() => setPatch(id, { stock: Math.max(0, (item.stock ?? 0) - 10) })}
                      >
                        −
                      </button>
                      <input
                        type="number"
                        inputMode="numeric"
                        value={item.stock ?? 0}
                        onInput={(e) => {
                          const v = parseInt(e.currentTarget.value, 10);
                          setPatch(id, { stock: Number.isFinite(v) ? Math.max(0, v) : 0 });
                        }}
                      />
                      <button
                        aria-label="mehr"
                        onClick={() => setPatch(id, { stock: (item.stock ?? 0) + 10 })}
                      >
                        +
                      </button>
                      <button class="av-limit-clear" aria-label="unbegrenzt" onClick={() => setPatch(id, { stock: null })}>
                        ∞
                      </button>
                    </div>
                  )}
                </div>

                <button
                  class={`av-toggle ${item.isAvailable ? "on" : "off"}`}
                  onClick={() => setPatch(id, { isAvailable: !item.isAvailable })}
                >
                  {item.isAvailable ? "Aktiv" : "Aus"}
                </button>
              </div>
            );
          })}
        </section>
      ))}

      {flash.value && <div class="admin-flash">{flash.value}</div>}

      {dirty.value && (
        <div class="av-savebar">
          <button class="btn-secondary" onClick={discard}>
            Verwerfen
          </button>
          <button class="btn-primary" disabled={saving.value} onClick={save}>
            {saving.value ? "Speichert…" : "Speichern"}
          </button>
        </div>
      )}
    </div>
  );
}
